import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { DEFAULT_DATE_RANGE, severityClass, trackDashboardEvent } from "../../constants/automapDealerDashboardConstants";
import { automapDealerPortalPath } from "../../constants/automapDealerPortalRoutes";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { fetchDealerDashboard } from "../../services/automapDealerDashboardApi";
import { openDealerWhatsapp } from "../../services/automapDealerLeadApi";
import DealerBuyerInterestSummaryWidget from "../../components/dealer/DealerBuyerInterestSummaryWidget";

function KpiCard({ label, value, hint, tone = "default" }) {
  return (
    <article className={`dealer-kpi-card dealer-kpi-card--${tone}`}>
      <span className="dealer-kpi-card__label">{label}</span>
      <strong className="dealer-kpi-card__value">{value ?? "—"}</strong>
      {hint ? <small className="dealer-kpi-card__hint">{hint}</small> : null}
    </article>
  );
}

function FunnelRow({ label, value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="dealer-funnel-row">
      <span className="dealer-funnel-row__label">{label}</span>
      <div className="dealer-funnel-row__track" aria-hidden="true">
        <div className="dealer-funnel-row__fill" style={{ width: `${pct}%` }} />
      </div>
      <strong className="dealer-funnel-row__value">{value}</strong>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="dealer-dashboard dealer-dashboard--loading" aria-busy="true">
      <div className="dealer-dashboard-hero dealer-skeleton-block" />
      <div className="dealer-kpi-grid">
        {[1, 2, 3, 4, 5, 6].map((id) => (
          <div key={id} className="dealer-skeleton-block dealer-skeleton-kpi" />
        ))}
      </div>
    </div>
  );
}

export default function DealerDashboardPage() {
  const { dealerId, dealerName } = useOutletContext();
  const {
    t,
    formatMoney,
    dateRangeOptions,
    formatLeadTemperature,
    translateError,
    localizeDashboard,
  } = useDealerPortalIntl();
  const [range, setRange] = useState(DEFAULT_DATE_RANGE);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerDashboard(dealerId, range);
      setDashboard(localizeDashboard(data.dashboard));
    } catch (err) {
      setError(translateError(err.message, "loadDashboard"));
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [dealerId, range, localizeDashboard, translateError]);

  useEffect(() => {
    trackDashboardEvent("dealer_dashboard_viewed", { dealerId, range });
  }, [dealerId, range]);

  useEffect(() => {
    load();
  }, [load]);

  const rangeLabel = useMemo(
    () => dateRangeOptions.find((option) => option.value === range)?.label || dateRangeOptions[2]?.label,
    [range, dateRangeOptions]
  );

  const handleRangeChange = (next) => {
    setRange(next);
    trackDashboardEvent("dealer_dashboard_date_range_changed", { range: next });
  };

  if (loading) return <DashboardSkeleton />;

  if (error) {
    return (
      <div className="dealer-dashboard">
        <div className="dealer-panel dealer-panel--error">
          <h2>{t("dashboard.loadErrorTitle")}</h2>
          <p className="muted">{error}</p>
          <button type="button" className="dealer-portal-btn primary" onClick={load}>
            {t("common.retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!dashboard) return null;

  const {
    summaryCards,
    leadMetrics,
    inventoryMetrics,
    hotLeads,
    topListings,
    alerts,
    checklist,
    profileHealth,
    buyerEngagement,
    performanceSummary,
    trialPanel,
    launchSummary,
    isEmpty,
    dealer,
    popularListings = [],
  } = dashboard;

  const priorityAlerts = alerts.filter((alert) => alert.severity !== "info").slice(0, 3);
  const openChecklist = checklist.filter((item) => !item.complete).slice(0, 5);
  const displayName = dealerName || dealer?.businessName || t("common.team");

  return (
    <div className="dealer-dashboard">
      <header className="dealer-dashboard-hero">
        <div className="dealer-dashboard-hero__copy">
          <p className="dealer-dashboard-hero__eyebrow">{t("dashboard.heroEyebrow")}</p>
          <h2>{t("dashboard.heroTitle", { name: displayName })}</h2>
          <p className="dealer-dashboard-hero__intro">
            {t("dashboard.heroIntro", { period: rangeLabel })}
          </p>
        </div>
        <div className="dealer-dashboard-hero__actions">
          <label className="dealer-range-control">
            <span>{t("common.period")}</span>
            <select value={range} onChange={(e) => handleRangeChange(e.target.value)}>
              {dateRangeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <Link className="dealer-portal-btn primary" to={automapDealerPortalPath("leads")}>
            {t("dashboard.viewLeads")}
          </Link>
          <Link className="dealer-portal-btn" to={automapDealerPortalPath("reportes")}>
            {t("dashboard.reports")}
          </Link>
        </div>
      </header>

      <div className="dealer-quick-actions">
        <Link className="dealer-quick-action" to={automapDealerPortalPath("leads")}>
          <strong>{t("dashboard.respondLeads")}</strong>
          <span>{t("common.newCount", { count: summaryCards.newLeads ?? 0 })}</span>
        </Link>
        <Link className="dealer-quick-action" to={automapDealerPortalPath("inventario/nuevo")}>
          <strong>{t("dashboard.addVehicle")}</strong>
          <span>{t("common.activeCount", { count: summaryCards.activeListings ?? 0 })}</span>
        </Link>
        <Link className="dealer-quick-action" to={automapDealerPortalPath("inventario")}>
          <strong>{t("dashboard.reviewInventory")}</strong>
          <span>{t("common.alertsCount", { count: summaryCards.inventoryIssues ?? 0 })}</span>
        </Link>
        {dealer?.slug ? (
          <a
            className="dealer-quick-action"
            href={`/v/automap/concesionarios/${dealer.slug}`}
            target="_blank"
            rel="noreferrer"
          >
            <strong>{t("dashboard.publicProfile")}</strong>
            <span>{t("common.viewOnAutoMapa")}</span>
          </a>
        ) : null}
      </div>

      {isEmpty ? (
        <section className="dealer-panel dealer-panel--highlight">
          <h3>{t("dashboard.emptyTitle")}</h3>
          <p className="muted">{t("dashboard.emptyBody")}</p>
          <div className="dealer-inline-actions">
            <Link className="dealer-portal-btn primary" to={automapDealerPortalPath("inventario/nuevo")}>
              {t("dashboard.addVehicle")}
            </Link>
            <Link className="dealer-portal-btn" to={automapDealerPortalPath("inventario/importar")}>
              {t("dashboard.importInventory")}
            </Link>
          </div>
        </section>
      ) : null}

      {(trialPanel || launchSummary || performanceSummary) && (
        <section className="dealer-panel dealer-panel--insight">
          <h3>{t("dashboard.insightTitle")}</h3>
          {trialPanel?.message ? <p>{trialPanel.message}</p> : null}
          {launchSummary?.responseReminder ? <p>{launchSummary.responseReminder}</p> : null}
          {performanceSummary?.recommendedActions?.[0] ? (
            <p className="muted">{performanceSummary.recommendedActions[0].message}</p>
          ) : null}
        </section>
      )}

      <div className="dealer-kpi-grid">
        <KpiCard label={t("dashboard.kpiActiveListings")} value={summaryCards.activeListings} tone="blue" />
        <KpiCard label={t("dashboard.kpiNewLeads")} value={summaryCards.newLeads} tone="accent" />
        <KpiCard label={t("dashboard.kpiHotLeads")} value={summaryCards.hotLeads} tone="warm" />
        <KpiCard label={t("dashboard.kpiLeadsInPeriod")} value={summaryCards.leadsThisMonth} />
        <KpiCard label={t("dashboard.kpiWhatsappClicks")} value={summaryCards.whatsappClicks} />
        <KpiCard
          label={t("dashboard.kpiResponseTime")}
          value={summaryCards.averageResponseTime || "—"}
          hint={summaryCards.averageResponseTimePlaceholder}
        />
      </div>

      <div className="dealer-dashboard-grid">
        <div className="dealer-dashboard-main">
          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("dashboard.hotLeadsTitle")}</h3>
              <Link to={automapDealerPortalPath("leads")}>{t("common.viewAll")}</Link>
            </div>
            {hotLeads.length === 0 ? (
              <p className="muted">{t("dashboard.hotLeadsEmpty")}</p>
            ) : (
              <ul className="dealer-hot-leads">
                {hotLeads.map((lead) => (
                  <li key={lead.id} className="dealer-hot-lead">
                    <div className="dealer-hot-lead__body">
                      <div className="dealer-hot-lead__title">
                        <strong>{lead.buyerName}</strong>
                        <span className="dealer-badge dealer-badge--hot">{lead.leadScore}/100</span>
                      </div>
                      <p className="muted">
                        {formatLeadTemperature(lead.leadTemperature)} · {lead.vehicleSummary} ·{" "}
                        {lead.purchaseTimeline || t("common.noTimeline")}
                      </p>
                    </div>
                    <div className="dealer-inline-actions">
                      <Link
                        className="dealer-portal-btn"
                        to={automapDealerPortalPath(`leads/${lead.id}`)}
                        onClick={() =>
                          trackDashboardEvent("dealer_dashboard_hot_lead_clicked", { leadId: lead.id })
                        }
                      >
                        {t("dashboard.viewLead")}
                      </Link>
                      {lead.dealerWhatsappUrl ? (
                        <button
                          type="button"
                          className="dealer-portal-btn primary"
                          onClick={() => openDealerWhatsapp(lead.dealerWhatsappUrl, { dealerId, leadId: lead.id })}
                        >
                          {t("common.whatsapp")}
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("dashboard.funnelTitle")}</h3>
              <span className="muted">{t("dashboard.funnelInPeriod", { count: leadMetrics.total })}</span>
            </div>
            <FunnelRow label={t("dashboard.funnelNew")} value={leadMetrics.funnel.new} max={leadMetrics.total || 1} />
            <FunnelRow label={t("dashboard.funnelContacted")} value={leadMetrics.funnel.contacted} max={leadMetrics.total || 1} />
            <FunnelRow label={t("dashboard.funnelTestDrive")} value={leadMetrics.funnel.testDrive} max={leadMetrics.total || 1} />
            <FunnelRow label={t("dashboard.funnelNegotiating")} value={leadMetrics.funnel.negotiating} max={leadMetrics.total || 1} />
            <FunnelRow label={t("dashboard.funnelSold")} value={leadMetrics.funnel.sold} max={leadMetrics.total || 1} />
            {leadMetrics.conversionRate != null ? (
              <p className="dealer-panel__footnote muted">
                {t("dashboard.conversionRate", { rate: leadMetrics.conversionRate })}
              </p>
            ) : null}
          </section>

          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("dashboard.topListingsTitle")}</h3>
              <Link to={automapDealerPortalPath("inventario")}>{t("nav.inventory")}</Link>
            </div>
            {topListings.length === 0 ? (
              <p className="muted">{t("dashboard.topListingsEmpty")}</p>
            ) : (
              <ul className="dealer-top-listings">
                {topListings.slice(0, 5).map((listing) => (
                  <li key={listing.id} className="dealer-top-listing">
                    {listing.primaryPhotoUrl ? (
                      <img src={listing.primaryPhotoUrl} alt="" className="dealer-top-listing__thumb" />
                    ) : (
                      <div className="dealer-top-listing__thumb dealer-top-listing__thumb--empty" />
                    )}
                    <div className="dealer-top-listing__copy">
                      <strong>{[listing.year, listing.make, listing.model].filter(Boolean).join(" ")}</strong>
                      <p className="muted">
                        {formatMoney(listing.priceCop)} · {t("common.quality")} {listing.qualityScore} · {listing.metricLabel}
                      </p>
                    </div>
                    <div className="dealer-inline-actions">
                      <Link
                        className="dealer-portal-btn"
                        to={listing.editPath}
                        onClick={() =>
                          trackDashboardEvent("dealer_dashboard_top_listing_clicked", { listingId: listing.id })
                        }
                      >
                        {t("common.edit")}
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <DealerBuyerInterestSummaryWidget dealerId={dealerId} />

          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("dashboard.popularTitle")}</h3>
              <Link to={automapDealerPortalPath("patrocinios")}>{t("dashboard.viewSponsorships")}</Link>
            </div>
            {popularListings.length === 0 ? (
              <p className="muted">{t("dashboard.popularEmpty")}</p>
            ) : (
              <ul className="dealer-top-listings">
                {popularListings.map((listing) => (
                  <li key={listing.listingId} className="dealer-top-listing">
                    {listing.primaryPhotoUrl ? (
                      <img src={listing.primaryPhotoUrl} alt="" className="dealer-top-listing__thumb" />
                    ) : (
                      <div className="dealer-top-listing__thumb dealer-top-listing__thumb--empty" />
                    )}
                    <div className="dealer-top-listing__copy">
                      <strong>{listing.vehicle}</strong>
                      <p className="muted">
                        <span className="dealer-like-count">♥ {listing.saveCount}</span>
                        {listing.listingViews ? ` · ${listing.listingViews} ${t("reports.views").toLowerCase()}` : ""}
                      </p>
                    </div>
                    <div className="dealer-inline-actions">
                      <Link className="dealer-portal-btn" to={automapDealerPortalPath(`patrocinios?listingId=${listing.listingId}`)}>
                        {t("sponsorships.boost")}
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="dealer-panel__footnote muted">{t("dashboard.popularHint")}</p>
          </section>
        </div>

        <aside className="dealer-dashboard-side">
          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("dashboard.checklistTitle")}</h3>
            </div>
            <ul className="dealer-checklist">
              {openChecklist.length === 0 ? (
                <li className="dealer-checklist__item dealer-checklist__item--complete">
                  {t("dashboard.checklistComplete")}
                </li>
              ) : (
                openChecklist.map((item) => (
                  <li key={item.id} className="dealer-checklist__item">
                    <Link
                      to={item.link}
                      onClick={() =>
                        trackDashboardEvent("dealer_dashboard_action_clicked", { actionId: item.id })
                      }
                    >
                      {item.label}
                      {item.count ? ` (${item.count})` : ""}
                    </Link>
                  </li>
                ))
              )}
            </ul>
          </section>

          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("dashboard.alertsTitle")}</h3>
            </div>
            {priorityAlerts.length === 0 ? (
              <p className="muted">{t("dashboard.alertsEmpty")}</p>
            ) : (
              <ul className="dealer-alert-list">
                {priorityAlerts.map((alert) => (
                  <li key={alert.id} className={`dealer-alert ${severityClass(alert.severity)}`}>
                    <span className="dealer-alert__badge">{alert.severity}</span>
                    <p>{alert.message}</p>
                    <Link
                      to={alert.link}
                      onClick={() =>
                        trackDashboardEvent("dealer_dashboard_inventory_alert_clicked", { alertId: alert.id })
                      }
                    >
                      {alert.actionLabel}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {profileHealth ? (
            <section className="dealer-panel">
              <div className="dealer-panel__header">
                <h3>{t("dashboard.profileHealthTitle")}</h3>
                <strong>{profileHealth.score}/100</strong>
              </div>
              <p className="muted">{profileHealth.label}</p>
              <ul className="dealer-checklist dealer-checklist--compact">
                {profileHealth.items.slice(0, 4).map((item) => (
                  <li
                    key={`${item.key}-${item.complete}`}
                    className={
                      item.complete
                        ? "dealer-checklist__item dealer-checklist__item--complete"
                        : "dealer-checklist__item"
                    }
                  >
                    {item.complete ? "✓" : "○"} {item.label}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="dealer-panel dealer-panel--muted">
            <h3>{t("dashboard.inventoryEngagementTitle")}</h3>
            <dl className="dealer-stat-list">
              <div>
                <dt>{t("dashboard.statActive")}</dt>
                <dd>{inventoryMetrics.active}</dd>
              </div>
              <div>
                <dt>{t("dashboard.statPublic")}</dt>
                <dd>{inventoryMetrics.publicVisible}</dd>
              </div>
              <div>
                <dt>{t("dashboard.statMissingPhotos")}</dt>
                <dd>{inventoryMetrics.missingPhotos}</dd>
              </div>
              <div>
                <dt>{t("dashboard.statListingViews")}</dt>
                <dd>{buyerEngagement?.listingViews ?? 0}</dd>
              </div>
            </dl>
          </section>
        </aside>
      </div>
    </div>
  );
}
